/*
 * FlightDetailsCard.jsx — Itinerary renderer for Booking / Ancillary / Review.
 *
 * Sprint-7 (Feb 2026 · FLIGHT_DETAILS_PAGES_RENDERING_AUDIT_REPORT.md):
 * Previous version read `segments[0]` only, hiding intermediate stops on
 * Ancillary + Review pages. Now poly-shape:
 *
 *   • One-way 1 segment   →  single SegmentBlock
 *   • One-way N segments  →  N SegmentBlocks + (N-1) layover dividers
 *   • Roundtrip           →  Onward leg + Return leg, each rendered like above
 *   • Combo (Tripjack RT) →  flight.onwardSegments / returnSegments handled
 *
 * Audit fixes shipped:
 *   F1 — full multi-segment rendering on every page
 *   F2 — baggage rows hide instead of showing fake `7 Kg` / `15 Kg` defaults
 */
import React from 'react';
import { Plane, Luggage, Utensils, ArrowRight, Clock } from 'lucide-react';
import { resolveTripjackSegmentBaggage } from '../utils/tripjackTripBaggage';

// ─── helpers ────────────────────────────────────────────────────────────
const fmtTime = (iso) => (iso || '').split('T')[1]?.substring(0, 5) || null;

const fmtDate = (iso) => {
  if (!iso) return null;
  const datePart = iso.includes('T') ? iso.split('T')[0] : iso;
  if (datePart.includes(' ')) return datePart;  // already formatted
  try {
    const d = new Date(datePart);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return datePart;
  }
};

const fmtDuration = (mins) => {
  const m = Number(mins || 0);
  if (!m) return null;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

// Layover between two consecutive segments — supplier doesn't always
// expose this; fall back to silent rendering when arrival→departure
// offsets aren't computable.
const layoverFor = (prev, next) => {
  try {
    const prevArr = prev?.at && new Date(prev.at);
    const nextDep = next?.dt && new Date(next.dt);
    if (!prevArr || !nextDep) return null;
    const diff = (nextDep.getTime() - prevArr.getTime()) / 60000;
    if (!isFinite(diff) || diff <= 0) return null;
    return fmtDuration(diff);
  } catch {
    return null;
  }
};

const pickSegments = (legOrFlight) => {
  if (!legOrFlight) return [];
  if (Array.isArray(legOrFlight.segments) && legOrFlight.segments.length > 0) {
    return legOrFlight.segments;
  }
  if (Array.isArray(legOrFlight.sI) && legOrFlight.sI.length > 0) {
    return legOrFlight.sI;
  }
  // Single-segment flat shape — wrap so the renderer can iterate uniformly.
  return [legOrFlight];
};

// ─── single-segment block ──────────────────────────────────────────────
const SegmentBlock = ({ segment, displayFlight, flightData, airlineLogos = {}, segIndex, totalSegs }) => {
  const depTime = fmtTime(segment?.dt) || segment?.departureTime || 'N/A';
  const arrTime = fmtTime(segment?.at) || segment?.arrivalTime || 'N/A';

  const travelDate =
    fmtDate(segment?.dt) ||
    fmtDate(segment?.departureDate) ||
    fmtDate(flightData?.route?.departureDate) ||
    fmtDate(flightData?.date) ||
    fmtDate(flightData?.departureDate) ||
    null;

  const airlineCode = segment?.fD?.aI?.code || segment?.airlineCode || flightData?.airlineCode;
  const airlineName = segment?.fD?.aI?.name || segment?.airline || flightData?.airline || 'Airline';
  const flightNumber = segment?.fD?.fN || segment?.flightNumber || flightData?.flightNumber || 'N/A';
  const aircraftType = segment?.fD?.eT || segment?.aircraftType || 'Aircraft';

  const depAirportCode = segment?.da?.code || segment?.from || segment?.source || flightData?.route?.from;
  const depAirportCity = segment?.da?.city || segment?.fromCity;
  const depTerminal = segment?.da?.terminal;

  const arrAirportCode = segment?.aa?.code || segment?.to || segment?.destination || flightData?.route?.to;
  const arrAirportCity = segment?.aa?.city || segment?.toCity;
  const arrTerminal = segment?.aa?.terminal;

  // Tripjack: fd.bI.iB = checked, fd.bI.cB = cabin; prefer tai.tbi per segment id.
  const { checked: checkInBaggage, cabin: cabinBaggage } = resolveTripjackSegmentBaggage(
    segment,
    displayFlight,
    flightData,
  );
  const hasBaggageInfo = !!(cabinBaggage || checkInBaggage);

  const hasMealField =
    segment?.ssrInfo?.MEAL !== undefined ||
    displayFlight?.mealIncluded !== undefined ||
    flightData?.mealIncluded !== undefined;
  const hasMeal =
    (segment?.ssrInfo?.MEAL?.length > 0) || displayFlight?.mealIncluded || flightData?.mealIncluded || false;

  const segDuration = segment?.duration || segment?.flightDuration || flightData?.duration || 0;
  const durationStr = fmtDuration(segDuration);

  return (
    <div className="bg-white" data-testid={`segment-block-${segIndex}`}>
      <div className="bg-gradient-to-r from-[#1B375B] to-[#335E77] px-3 py-1.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
            {airlineLogos[airlineCode]?.square || airlineLogos[airlineCode]?.best ? (
              <img
                src={airlineLogos[airlineCode].square || airlineLogos[airlineCode].best}
                alt={airlineName}
                className="w-full h-full object-contain"
              />
            ) : (
              <Plane className="w-4 h-4 text-white" />
            )}
          </div>
          <div>
            <p className="text-xs font-bold text-white leading-tight">
              {airlineName} • {flightNumber}
            </p>
            <p className="text-[9px] text-gray-300 leading-tight">
              {aircraftType}{durationStr ? ` • ${durationStr}` : ''}
            </p>
          </div>
        </div>
        {totalSegs > 1 && (
          <span className="text-[9px] font-semibold text-white/90 bg-white/15 rounded px-1.5 py-0.5">
            Segment {segIndex + 1} of {totalSegs}
          </span>
        )}
      </div>

      <div className="p-3">
        <div className="grid grid-cols-[1fr_auto_1fr] gap-3">
          <div>
            <p className="text-[9px] text-gray-500 uppercase mb-0.5">Departure</p>
            <p className="text-2xl font-black text-[#1B375B] leading-none mb-0.5">{depTime}</p>
            <p className="text-xs font-bold text-gray-800 leading-tight">{depAirportCode}</p>
            <p className="text-[10px] text-gray-600 leading-tight">{depAirportCity}</p>
            {depTerminal && (
              <span className="inline-block mt-0.5 text-[8px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">
                {depTerminal}
              </span>
            )}
          </div>

          <div className="flex flex-col items-center justify-center px-1">
            <div className="flex flex-col items-center gap-0.5">
              <div className="w-1.5 h-1.5 rounded-full border-2 border-[#1B375B]" />
              <div className="h-8 w-[2px] bg-gradient-to-b from-[#1B375B] to-[#659A9B]" />
              <Plane className="w-4 h-4 text-[#659A9B] rotate-90" />
              <div className="h-8 w-[2px] bg-gradient-to-b from-[#659A9B] to-[#1B375B]" />
              <div className="w-1.5 h-1.5 rounded-full bg-[#659A9B]" />
            </div>
          </div>

          <div className="text-right">
            <p className="text-[9px] text-gray-500 uppercase mb-0.5">Arrival</p>
            <p className="text-2xl font-black text-[#1B375B] leading-none mb-0.5">{arrTime}</p>
            <p className="text-xs font-bold text-gray-800 leading-tight">{arrAirportCode}</p>
            <p className="text-[10px] text-gray-600 leading-tight">{arrAirportCity}</p>
            {arrTerminal && (
              <span className="inline-block mt-0.5 text-[8px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">
                {arrTerminal}
              </span>
            )}
          </div>
        </div>

        {/* Sprint-7 F2 · Compact info row — date always shown, baggage and
            meal rows render only when supplier surfaced real values. */}
        <div className="flex flex-wrap items-center gap-3 mt-2 pt-2 border-t border-gray-200">
          {travelDate && (
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-[8px] text-gray-500 uppercase font-semibold leading-tight">Date</p>
                <p className="text-[10px] font-bold text-gray-800 truncate leading-tight">{travelDate}</p>
              </div>
            </div>
          )}

          {hasBaggageInfo && (
            <div className="flex items-center gap-1.5" data-testid={`segment-baggage-${segIndex}`}>
              <div className="w-6 h-6 rounded bg-[#659A9B]/10 flex items-center justify-center flex-shrink-0">
                <Luggage className="w-3.5 h-3.5 text-[#659A9B]" />
              </div>
              <div className="min-w-0">
                <p className="text-[8px] text-gray-500 uppercase font-semibold leading-tight">Baggage</p>
                {cabinBaggage && (
                  <p className="text-[10px] font-bold text-gray-800 truncate leading-tight">Cabin: {cabinBaggage}</p>
                )}
                {checkInBaggage && (
                  <p className="text-[9px] text-gray-600 truncate leading-tight">Check-in: {checkInBaggage}</p>
                )}
              </div>
            </div>
          )}

          {hasMealField && (
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Utensils className="w-3.5 h-3.5 text-orange-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[8px] text-gray-500 uppercase font-semibold leading-tight">Meal</p>
                <p className="text-[10px] font-bold text-gray-800 leading-tight">
                  {hasMeal ? 'Included' : 'Not Included'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── layover divider between segments ──────────────────────────────────
const LayoverDivider = ({ stopCity, stopCode, layoverDur }) => (
  <div
    className="my-2 mx-3 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-md flex items-center justify-between"
    data-testid="layover-divider"
  >
    <div className="flex items-center gap-1.5">
      <Clock className="w-3.5 h-3.5 text-amber-700" />
      <span className="text-[10px] font-semibold text-amber-900">
        Layover at {stopCity || stopCode || 'connection'}
      </span>
    </div>
    {layoverDur && (
      <span className="text-[10px] font-bold text-amber-900">{layoverDur}</span>
    )}
  </div>
);

// ─── leg renderer (handles N segments + (N-1) layover dividers) ────────
const LegRenderer = ({ leg, label, displayFlight, flightData, airlineLogos, legKey }) => {
  const segments = pickSegments(leg);
  if (segments.length === 0) return null;

  return (
    <div data-testid={`leg-${legKey || 'oneway'}`} className="space-y-0">
      {label && (
        <div className="px-3 py-1 bg-gray-100 border-b border-gray-200">
          <p className="text-[10px] font-bold text-[#1B375B] uppercase tracking-wider">{label}</p>
        </div>
      )}
      {segments.map((seg, i) => {
        const isLast = i === segments.length - 1;
        const layoverDur = !isLast ? layoverFor(seg, segments[i + 1]) : null;
        const stopCity = !isLast ? (seg?.aa?.city || segments[i + 1]?.da?.city) : null;
        const stopCode = !isLast ? (seg?.aa?.code || segments[i + 1]?.da?.code) : null;
        return (
          <React.Fragment key={`${legKey || 'leg'}-${i}`}>
            <SegmentBlock
              segment={seg}
              displayFlight={displayFlight}
              flightData={flightData}
              airlineLogos={airlineLogos}
              segIndex={i}
              totalSegs={segments.length}
            />
            {!isLast && (
              <LayoverDivider stopCity={stopCity} stopCode={stopCode} layoverDur={layoverDur} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ─── shape detection ───────────────────────────────────────────────────
const detectShape = (flightData) => {
  if (!flightData) return { kind: 'empty' };
  // Roundtrip with onward + return at top level
  if (flightData.onward && flightData.return) {
    return { kind: 'rt', onward: flightData.onward, ret: flightData.return };
  }
  // Combo wrapper (Tripjack INTL RT)
  if (flightData.flight?.onwardSegments || flightData.flight?.returnSegments) {
    return {
      kind: 'rt',
      onward: { segments: flightData.flight.onwardSegments || [] },
      ret: { segments: flightData.flight.returnSegments || [] },
    };
  }
  // Plain leg or flight
  return { kind: 'leg', leg: flightData };
};

// ─── main export ────────────────────────────────────────────────────────
const FlightDetailsCard = ({ flightData, displayFlight, airlineLogos = {} }) => {
  const shape = detectShape(flightData);

  if (shape.kind === 'empty') {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
        <p className="text-xs text-gray-500">Flight details unavailable</p>
      </div>
    );
  }

  const wrapperClass = 'bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm';

  if (shape.kind === 'rt') {
    const retSegs = pickSegments(shape.ret);
    return (
      <div className={`${wrapperClass} divide-y divide-gray-200`} data-testid="flight-details-card-rt">
        <LegRenderer
          leg={shape.onward}
          label="Onward"
          displayFlight={displayFlight}
          flightData={flightData}
          airlineLogos={airlineLogos}
          legKey="onward"
        />
        {retSegs.length > 0 && (
          <LegRenderer
            leg={shape.ret}
            label="Return"
            displayFlight={displayFlight}
            flightData={flightData}
            airlineLogos={airlineLogos}
            legKey="return"
          />
        )}
      </div>
    );
  }

  return (
    <div className={wrapperClass} data-testid="flight-details-card">
      <LegRenderer
        leg={shape.leg}
        label={null}
        displayFlight={displayFlight}
        flightData={flightData}
        airlineLogos={airlineLogos}
        legKey="oneway"
      />
    </div>
  );
};

// Suppress unused-var on imported icons that are referenced inside conditional renders
// eslint-disable-next-line no-unused-vars
const _unused = ArrowRight;

export default FlightDetailsCard;
